import * as React from "react";
import Loadable from "react-loadable";
import { RouteComponentProps } from "react-router-dom";

import { ROUTES } from "@config/app.routing";

import { SERVICE_TYPE } from "@typings/globals";
import { LOADING_STATE } from "@typings/reduxThunkActions";

import { ImpersonatorNotification } from "@ui/Molecules";

import { getQueryValue } from "@services/QueryParamsParser";

import { useAuth } from "@hooks/useAuth";
import { useBreakpoint } from "@hooks/useBreakpoint";
import { useLocalisation } from "@hooks/useLocalisation";
import {
  FEATURE_TOGGLE_ORIGIN,
  useFeatureToggleManager,
} from "@hooks/useFeatureToggle";

import { leaveProcess, setServiceLocationId } from "@app/redux/b2b/b2b.actions";
import {
  resetState as resetServiceLocationState,
  fetchServiceLocationCoverage,
} from "@app/redux/serviceLocation/serviceLocation.actions";
import { ServiceTypeAvailabilityChecker } from "@app/containers/InactiveServiceTypeChecker";
import { useAppDispatch } from "@app/redux/hooks";

import { ProcessSkeleton } from "./B2B.skeletons";

const LoadableGetAQuoteProcess = Loadable({
  loader: () => import("./GetAQuoteProcess"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.GetAQuoteProcess;
    return <Component {...props} />;
  },
  loading: ProcessSkeleton,
});

const LoadableBookingProcess = Loadable({
  loader: () => import("./BookingProcess"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.BookingProcess;
    return <Component {...props} />;
  },
  loading: ProcessSkeleton,
});

const B2B = (props: RouteComponentProps): React.ReactElement | null => {
  const dispatch = useAppDispatch();

  const { isUserAdmin, isAdminImpersonator } = useAuth();
  const { language } = useLocalisation();

  const { desktop, desktopLarge } = useBreakpoint("v1");
  const renderSidebar = desktop || desktopLarge;

  const { search, pathname } = props.history.location;

  const zip = getQueryValue(search, "postalCode");
  const isFirstPage = pathname === ROUTES.B2B;
  const isGetAQuote =
    pathname === ROUTES.B2B_GET_A_QUOTE || pathname === ROUTES.B2B_THANK_YOU;

  const { origin: featureToggleOrigin, status: featureToggleStatus } =
    useFeatureToggleManager();
  const featureTogglesAreReady =
    featureToggleOrigin !== FEATURE_TOGGLE_ORIGIN.DEFAULT ||
    [LOADING_STATE.SUCCEEDED, LOADING_STATE.FAILED].includes(
      featureToggleStatus,
    );

  React.useEffect(() => {
    (async () => {
      if (zip) {
        const { onSuccess } = await dispatch(
          fetchServiceLocationCoverage(zip, undefined, { saveData: true }),
        );

        onSuccess(payload => {
          dispatch(setServiceLocationId(payload.data.serviceLocationId!));
        });
      }
    })();
  }, [zip, dispatch]);

  React.useEffect(() => {
    return () => {
      dispatch(leaveProcess());
      dispatch(resetServiceLocationState());
    };
  }, []);

  const handleInvalidServiceLocationId = (): void => {
    window.location.href = `/${language}/`;
  };

  // redirect before any rendering logic begins
  if (!zip && isFirstPage) {
    handleInvalidServiceLocationId();
    return null;
  }

  if (isUserAdmin && !isAdminImpersonator) {
    return <ImpersonatorNotification />;
  }

  return (
    <React.Fragment>
      <ServiceTypeAvailabilityChecker
        serviceType={SERVICE_TYPE.B2B}
        postalCodeQuery={String(zip)}
      />
      {!featureTogglesAreReady && <ProcessSkeleton />}

      {isGetAQuote && featureTogglesAreReady && (
        <LoadableGetAQuoteProcess renderSidebar={renderSidebar} />
      )}
      {!isGetAQuote && featureTogglesAreReady && (
        <LoadableBookingProcess renderSidebar={renderSidebar} />
      )}
    </React.Fragment>
  );
};

export { B2B };
